import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import AccountActions from "../comp/AccountActions";
import {
  exportReferenceValues,
  getReferenceValues,
  importReferenceValues,
  saveReferenceValues,
} from "../api/referenceValues";
import {
  decodePayload,
  getToken,
  logout,
  startExpiryTimer,
  tokenExpired,
} from "../util/auth";

type AgeAdjustment = {
  latency_add?: number | null;
  cv_subtract?: number | null;
  amp_subtract?: number | null;
  latency_value?: number | null;
  cv_value?: number | null;
  amplitude_value?: number | null;
};

type ReferenceMeta = {
  age_bands?: string[];
};

type ReferenceSegment = {
  nerve?: string;
  stimulation_site?: string;
  recording_site?: string;
  latency_ms?: number | null;
  amplitude_mv?: number | null;
  amplitude_uv?: number | null;
  cv_mps?: number | null;
  age_adjustment?: Record<string, AgeAdjustment>;
  [key: string]: any;
};

type ReferenceValues = {
  motor_ncs?: Record<string, { segments?: ReferenceSegment[] }>;
  sensory_ncs?: ReferenceSegment[];
  special_ncs?: {
    F_wave?: ReferenceSegment[];
    H_reflex?: ReferenceSegment[];
  };
  meta?: ReferenceMeta;
};

type ReferenceResponse = {
  profile_id: string;
  profile_name: string;
  rule_set: string;
  owner_username: string | null;
  updated_at: string | null;
  values: ReferenceValues;
};

type TabKey = "motor" | "sensory" | "fwave" | "hreflex";
type AgeBandDraft = { min: string; max: string };
type AgeMetric = "latency" | "amplitude" | "cv";

const INITIAL_AGE_BANDS = ["0-59", "60+"];

const cloneValues = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const toInputValue = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

const parseNullableNumber = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const next = Number(trimmed);
  return Number.isNaN(next) ? null : next;
};

const matchesSearch = (search: string, parts: Array<string | undefined>) => {
  if (!search.trim()) return true;
  const needle = search.trim().toLowerCase();
  return parts.some((part) => (part || "").toLowerCase().includes(needle));
};

const formatSavedAt = (value: string | null) => {
  if (!value) return "Not saved yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not saved yet";
  return new Intl.DateTimeFormat("ko-KR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const parseAgeBandKey = (key: string): AgeBandDraft => {
  const trimmed = (key || "").trim();
  if (!trimmed) return { min: "", max: "" };

  if (trimmed.endsWith("+")) {
    return { min: trimmed.slice(0, -1), max: "" };
  }

  const [min = "", max = ""] = trimmed.split("-");
  return { min, max };
};

const buildAgeBandKey = (minRaw: string, maxRaw: string) => {
  const min = Number(minRaw.trim());
  if (Number.isNaN(min) || min < 0) return null;

  const maxText = maxRaw.trim();
  if (!maxText) return `${Math.floor(min)}+`;

  const max = Number(maxText);
  if (Number.isNaN(max) || max < min) return null;
  return `${Math.floor(min)}-${Math.floor(max)}`;
};

const sortAgeBandKeys = (keys: string[]) =>
  [...keys].sort((left, right) => {
    const leftRange = parseAgeBandKey(left);
    const rightRange = parseAgeBandKey(right);
    const leftMin = Number(leftRange.min || 0);
    const rightMin = Number(rightRange.min || 0);
    if (leftMin !== rightMin) return leftMin - rightMin;

    const leftMax = leftRange.max === "" ? Number.POSITIVE_INFINITY : Number(leftRange.max);
    const rightMax = rightRange.max === "" ? Number.POSITIVE_INFINITY : Number(rightRange.max);
    return leftMax - rightMax;
  });

const ensureReferenceMeta = (values: ReferenceValues) => {
  if (!values.meta || typeof values.meta !== "object") {
    values.meta = {};
  }
  if (!Array.isArray(values.meta.age_bands)) {
    values.meta.age_bands = [];
  }
  return values.meta;
};

const ensureAgeAdjustments = (segment: ReferenceSegment) => {
  if (!segment.age_adjustment || typeof segment.age_adjustment !== "object") {
    segment.age_adjustment = {};
  }
  return segment.age_adjustment;
};

const visitAllSegments = (
  values: ReferenceValues,
  visitor: (segment: ReferenceSegment) => void
) => {
  Object.values(values.motor_ncs || {}).forEach((nerveData) => {
    (nerveData?.segments || []).forEach((segment) => visitor(segment));
  });

  (values.sensory_ncs || []).forEach((segment) => visitor(segment));
  (values.special_ncs?.F_wave || []).forEach((segment) => visitor(segment));
  (values.special_ncs?.H_reflex || []).forEach((segment) => visitor(segment));
};

const getConfiguredAgeBands = (values: ReferenceValues | null) =>
  sortAgeBandKeys(Array.from(new Set(values?.meta?.age_bands || [])));

const getAgeBandValue = (
  segment: ReferenceSegment,
  bandKey: string,
  metric: AgeMetric,
  amplitudeField?: "amplitude_mv" | "amplitude_uv"
) => {
  const entry = segment.age_adjustment?.[bandKey] || {};

  if (metric === "latency") {
    if (entry.latency_value !== null && entry.latency_value !== undefined) {
      return entry.latency_value;
    }
    if (segment.latency_ms !== null && segment.latency_ms !== undefined) {
      return Number(segment.latency_ms) + Number(entry.latency_add || 0);
    }
    if (entry.latency_add !== null && entry.latency_add !== undefined) {
      return Number(entry.latency_add);
    }
    return null;
  }

  if (metric === "amplitude") {
    if (entry.amplitude_value !== null && entry.amplitude_value !== undefined) {
      return entry.amplitude_value;
    }
    const baseValue = amplitudeField ? segment[amplitudeField] : null;
    if (baseValue !== null && baseValue !== undefined) {
      return Math.max(Number(baseValue) - Number(entry.amp_subtract || 0), 0);
    }
    return null;
  }

  if (entry.cv_value !== null && entry.cv_value !== undefined) {
    return entry.cv_value;
  }
  if (segment.cv_mps !== null && segment.cv_mps !== undefined) {
    return Number(segment.cv_mps) - Number(entry.cv_subtract || 0);
  }
  return null;
};

const getBaseMetricValue = (
  segment: ReferenceSegment,
  metric: AgeMetric,
  amplitudeField?: "amplitude_mv" | "amplitude_uv"
) => {
  if (metric === "latency") return segment.latency_ms ?? null;
  if (metric === "amplitude") return amplitudeField ? segment[amplitudeField] ?? null : null;
  return segment.cv_mps ?? null;
};

const setBaseMetricValue = (
  segment: ReferenceSegment,
  metric: AgeMetric,
  rawValue: string,
  amplitudeField?: "amplitude_mv" | "amplitude_uv"
) => {
  const nextValue = parseNullableNumber(rawValue);

  if (metric === "latency") {
    segment.latency_ms = nextValue;
    return;
  }

  if (metric === "amplitude" && amplitudeField) {
    segment[amplitudeField] = nextValue;
    return;
  }

  if (metric === "cv") {
    segment.cv_mps = nextValue;
  }
};

const setAgeBandMetricValue = (
  segment: ReferenceSegment,
  bandKey: string,
  metric: AgeMetric,
  rawValue: string
) => {
  const adjustments = ensureAgeAdjustments(segment);
  const entry = { ...(adjustments[bandKey] || {}) };
  const value = parseNullableNumber(rawValue);

  if (metric === "latency") {
    if (value === null) {
      delete entry.latency_value;
      delete entry.latency_add;
    } else {
      entry.latency_value = value;
      delete entry.latency_add;
    }
  }

  if (metric === "amplitude") {
    if (value === null) {
      delete entry.amplitude_value;
      delete entry.amp_subtract;
    } else {
      entry.amplitude_value = value;
      delete entry.amp_subtract;
    }
  }

  if (metric === "cv") {
    if (value === null) {
      delete entry.cv_value;
      delete entry.cv_subtract;
    } else {
      entry.cv_value = value;
      delete entry.cv_subtract;
    }
  }

  if (Object.keys(entry).length) {
    adjustments[bandKey] = entry;
  } else {
    delete adjustments[bandKey];
  }

  if (!Object.keys(adjustments).length) {
    delete segment.age_adjustment;
  }
};

const splitBandKey = (bandKey: string | null) => {
  if (!bandKey) {
    return {
      replacement: INITIAL_AGE_BANDS,
      selected: INITIAL_AGE_BANDS[0],
    };
  }

  const parsed = parseAgeBandKey(bandKey);
  const min = Number(parsed.min);
  if (Number.isNaN(min)) return null;

  if (parsed.max === "") {
    const leftMax = min + 19;
    const rightMin = leftMax + 1;
    return {
      replacement: [`${min}-${leftMax}`, `${rightMin}+`],
      selected: `${min}-${leftMax}`,
    };
  }

  const max = Number(parsed.max);
  if (Number.isNaN(max) || max <= min) return null;

  const midpoint = Math.floor((min + max) / 2);
  const left = `${min}-${midpoint}`;
  const right = `${midpoint + 1}-${max}`;

  return {
    replacement: [left, right],
    selected: left,
  };
};

const ReferenceValuesPage: React.FC = () => {
  const navigate = useNavigate();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<TabKey>("motor");
  const [savedProfile, setSavedProfile] = useState<ReferenceResponse | null>(null);
  const [draftValues, setDraftValues] = useState<ReferenceValues | null>(null);
  const [activeAgeBand, setActiveAgeBand] = useState<string | null>(null);
  const [bandDrafts, setBandDrafts] = useState<Record<string, AgeBandDraft>>({});

  useEffect(() => {
    if (tokenExpired()) {
      alert("Login required.");
      logout(true);
      window.location.href = "/login";
      return;
    }

    startExpiryTimer();

    const token = getToken();
    if (token) {
      try {
        const payload = decodePayload(token);
        setUsername(payload.username || payload.sub || "");
      } catch {
        // noop
      }
    }

    return () => {
      const win = window as Window & { __expiryTimer?: number };
      if (win.__expiryTimer) clearTimeout(win.__expiryTimer);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getReferenceValues();
        const nextValues = cloneValues(data.values);
        setSavedProfile(data);
        setDraftValues(nextValues);

        const nextAgeBands = getConfiguredAgeBands(nextValues);
        setActiveAgeBand(nextAgeBands[0] || null);
      } catch (err) {
        console.error(err);
        setError("Failed to load reference values.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const ageBands = useMemo(() => getConfiguredAgeBands(draftValues), [draftValues]);

  useEffect(() => {
    if (!ageBands.length) {
      if (activeAgeBand !== null) setActiveAgeBand(null);
      return;
    }

    if (!activeAgeBand || !ageBands.includes(activeAgeBand)) {
      setActiveAgeBand(ageBands[0]);
    }
  }, [activeAgeBand, ageBands]);

  const hasChanges = useMemo(() => {
    if (!savedProfile || !draftValues) return false;
    return JSON.stringify(savedProfile.values) !== JSON.stringify(draftValues);
  }, [draftValues, savedProfile]);

  const actionsBusy = saving || exporting || importing;

  const motorRows = useMemo(() => {
    const motor = draftValues?.motor_ncs || {};
    return Object.entries(motor)
      .flatMap(([nerve, nerveData]) =>
        (nerveData?.segments || []).map((segment: ReferenceSegment, index: number) => ({
          rowKey: `motor:${nerve}:${index}`,
          nerve,
          index,
          segment,
        }))
      )
      .filter(({ nerve, segment }) =>
        matchesSearch(search, [nerve, segment.stimulation_site, segment.recording_site])
      );
  }, [draftValues, search]);

  const sensoryRows = useMemo(() => {
    return (draftValues?.sensory_ncs || [])
      .map((segment: ReferenceSegment, index: number) => ({
        rowKey: `sensory:${index}`,
        index,
        segment,
      }))
      .filter(({ segment }) =>
        matchesSearch(search, [segment.nerve, segment.stimulation_site, segment.recording_site])
      );
  }, [draftValues, search]);

  const fwaveRows = useMemo(() => {
    return (draftValues?.special_ncs?.F_wave || [])
      .map((segment: ReferenceSegment, index: number) => ({
        rowKey: `fwave:${index}`,
        index,
        segment,
      }))
      .filter(({ segment }) =>
        matchesSearch(search, [segment.nerve, segment.stimulation_site, segment.recording_site])
      );
  }, [draftValues, search]);

  const hreflexRows = useMemo(() => {
    return (draftValues?.special_ncs?.H_reflex || [])
      .map((segment: ReferenceSegment, index: number) => ({
        rowKey: `hreflex:${index}`,
        index,
        segment,
      }))
      .filter(({ segment }) =>
        matchesSearch(search, [segment.nerve, segment.stimulation_site, segment.recording_site])
      );
  }, [draftValues, search]);

  const tabItems = [
    { key: "motor" as const, label: "Motor", count: motorRows.length },
    { key: "sensory" as const, label: "Sensory", count: sensoryRows.length },
    { key: "fwave" as const, label: "F-wave", count: fwaveRows.length },
    { key: "hreflex" as const, label: "H-reflex", count: hreflexRows.length },
  ];

  const activeAgeDraft =
    activeAgeBand ? bandDrafts[activeAgeBand] || parseAgeBandKey(activeAgeBand) : null;

  const applyDraftMutation = (mutate: (next: ReferenceValues) => void) => {
    setDraftValues((prev) => {
      if (!prev) return prev;
      const next = cloneValues(prev);
      mutate(next);
      return next;
    });
  };

  const updateMotorSegment = (
    nerve: string,
    index: number,
    updater: (segment: ReferenceSegment) => void
  ) => {
    applyDraftMutation((next) => {
      const target = next.motor_ncs?.[nerve]?.segments?.[index];
      if (target) updater(target);
    });
  };

  const updateSensorySegment = (index: number, updater: (segment: ReferenceSegment) => void) => {
    applyDraftMutation((next) => {
      const target = next.sensory_ncs?.[index];
      if (target) updater(target);
    });
  };

  const updateSpecialSegment = (
    section: "F_wave" | "H_reflex",
    index: number,
    updater: (segment: ReferenceSegment) => void
  ) => {
    applyDraftMutation((next) => {
      const target = next.special_ncs?.[section]?.[index];
      if (target) updater(target);
    });
  };

  const setDisplayedMetricValue = (
    segment: ReferenceSegment,
    metric: AgeMetric,
    rawValue: string,
    amplitudeField?: "amplitude_mv" | "amplitude_uv"
  ) => {
    if (!activeAgeBand) {
      setBaseMetricValue(segment, metric, rawValue, amplitudeField);
      return;
    }

    setAgeBandMetricValue(segment, activeAgeBand, metric, rawValue);
  };

  const getDisplayedMetricValue = (
    segment: ReferenceSegment,
    metric: AgeMetric,
    amplitudeField?: "amplitude_mv" | "amplitude_uv"
  ) => {
    if (!activeAgeBand) {
      return getBaseMetricValue(segment, metric, amplitudeField);
    }

    return getAgeBandValue(segment, activeAgeBand, metric, amplitudeField);
  };

  const handleBandDraftChange = (patch: Partial<AgeBandDraft>) => {
    if (!activeAgeBand) return;
    const current = bandDrafts[activeAgeBand] || parseAgeBandKey(activeAgeBand);
    setBandDrafts((prev) => ({
      ...prev,
      [activeAgeBand]: { ...current, ...patch },
    }));
  };

  const commitActiveBandRange = () => {
    if (!activeAgeBand) return;

    const currentDraft = bandDrafts[activeAgeBand] || parseAgeBandKey(activeAgeBand);
    const nextKey = buildAgeBandKey(currentDraft.min, currentDraft.max);

    if (!nextKey) {
      setError("Age band range is not valid.");
      return;
    }

    if (nextKey === activeAgeBand) return;

    if (ageBands.includes(nextKey)) {
      setError("That age band already exists.");
      return;
    }

    applyDraftMutation((next) => {
      const meta = ensureReferenceMeta(next);
      meta.age_bands = sortAgeBandKeys(
        (meta.age_bands || []).map((bandKey) => (bandKey === activeAgeBand ? nextKey : bandKey))
      );

      visitAllSegments(next, (segment) => {
        const adjustments = segment.age_adjustment;
        if (!adjustments?.[activeAgeBand]) return;

        const currentEntry = { ...adjustments[activeAgeBand] };
        delete adjustments[activeAgeBand];
        adjustments[nextKey] = { ...(adjustments[nextKey] || {}), ...currentEntry };
      });
    });

    setBandDrafts((prev) => {
      const next = { ...prev };
      delete next[activeAgeBand];
      next[nextKey] = { ...currentDraft };
      return next;
    });
    setActiveAgeBand(nextKey);
    setError("");
    setSuccess("");
  };

  const handleSplitCurrentTab = () => {
    const split = splitBandKey(activeAgeBand);

    if (!split) {
      setError("This age band cannot be split further.");
      return;
    }

    applyDraftMutation((next) => {
      const meta = ensureReferenceMeta(next);
      const currentBands = meta.age_bands || [];

      if (!activeAgeBand) {
        meta.age_bands = split.replacement;
        return;
      }

      const nextBands = currentBands.flatMap((bandKey) =>
        bandKey === activeAgeBand ? split.replacement : [bandKey]
      );
      meta.age_bands = sortAgeBandKeys(nextBands);

      visitAllSegments(next, (segment) => {
        const currentEntry = segment.age_adjustment?.[activeAgeBand];
        if (!currentEntry) return;

        const adjustments = ensureAgeAdjustments(segment);
        delete adjustments[activeAgeBand];
        adjustments[split.replacement[0]] = cloneValues(currentEntry);
        adjustments[split.replacement[1]] = cloneValues(currentEntry);
      });
    });

    setBandDrafts((prev) => {
      const next = { ...prev };
      if (activeAgeBand) {
        delete next[activeAgeBand];
      }
      next[split.replacement[0]] = parseAgeBandKey(split.replacement[0]);
      next[split.replacement[1]] = parseAgeBandKey(split.replacement[1]);
      return next;
    });
    setActiveAgeBand(split.selected);
    setError("");
    setSuccess("");
  };

  const handleResetToUniversal = () => {
    applyDraftMutation((next) => {
      const meta = ensureReferenceMeta(next);
      meta.age_bands = [];

      visitAllSegments(next, (segment) => {
        delete segment.age_adjustment;
      });
    });

    setActiveAgeBand(null);
    setBandDrafts({});
    setError("");
    setSuccess("");
  };

  const handleReload = () => {
    if (!savedProfile) return;
    const nextValues = cloneValues(savedProfile.values);
    setDraftValues(nextValues);
    setBandDrafts({});
    setError("");
    setSuccess("");
    setActiveAgeBand(getConfiguredAgeBands(nextValues)[0] || null);
  };

  const handleSave = async () => {
    if (!draftValues) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const data = await saveReferenceValues(draftValues as Record<string, any>);
      const nextValues = cloneValues(data.values);
      setSavedProfile(data);
      setDraftValues(nextValues);
      setBandDrafts({});
      setSuccess("Reference values saved.");
      setActiveAgeBand(getConfiguredAgeBands(nextValues)[0] || null);
    } catch (err) {
      console.error(err);
      setError("Failed to save reference values.");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError("");
      setSuccess("");
      const payload = await exportReferenceValues();
      const safeProfileName = (payload.profile.profile_name || payload.profile.rule_set || "reference-values")
        .replace(/[^\w.-]+/g, "_");
      const timestamp = new Date().toISOString().replace(/[:]/g, "-");
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${safeProfileName}.${timestamp}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setSuccess("Reference values exported.");
    } catch (err) {
      console.error(err);
      setError("Failed to export reference values.");
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (hasChanges) {
      const proceed = window.confirm(
        "You have unsaved changes. Importing will replace the current draft. Continue?"
      );
      if (!proceed) return;
    }

    try {
      setImporting(true);
      setError("");
      setSuccess("");
      const raw = await file.text();
      const payload = JSON.parse(raw);
      const data = await importReferenceValues(payload);
      const nextValues = cloneValues(data.values);
      setSavedProfile(data);
      setDraftValues(nextValues);
      setBandDrafts({});
      setActiveAgeBand(getConfiguredAgeBands(nextValues)[0] || null);
      setSuccess("Reference values imported.");
    } catch (err) {
      console.error(err);
      if (err instanceof SyntaxError) {
        setError("The selected file is not valid JSON.");
      } else if (err instanceof Error) {
        setError(err.message || "Failed to import reference values.");
      } else {
        setError("Failed to import reference values.");
      }
    } finally {
      setImporting(false);
    }
  };

  const renderNumberInput = (value: unknown, onChange: (next: string) => void) => (
    <input
      type="number"
      step="any"
      className="table-input ref-number-input"
      value={toInputValue(value)}
      onChange={(event) => onChange(event.target.value)}
    />
  );

  const renderMotorTable = () => (
    <div className="table-shell ref-table-shell">
      <table className="app-table ref-table">
        <thead>
          <tr>
            <th>Nerve</th>
            <th>Stim</th>
            <th>Rec</th>
            <th>Lat(ms)</th>
            <th>Amp(mV)</th>
            <th>CV(m/s)</th>
          </tr>
        </thead>
        <tbody>
          {motorRows.map(({ rowKey, nerve, index, segment }) => (
            <tr key={rowKey}>
              <td>{nerve}</td>
              <td>{segment.stimulation_site}</td>
              <td>{segment.recording_site}</td>
              <td>
                {renderNumberInput(getDisplayedMetricValue(segment, "latency"), (value) =>
                  updateMotorSegment(nerve, index, (target) => {
                    setDisplayedMetricValue(target, "latency", value);
                  })
                )}
              </td>
              <td>
                {renderNumberInput(
                  getDisplayedMetricValue(segment, "amplitude", "amplitude_mv"),
                  (value) =>
                    updateMotorSegment(nerve, index, (target) => {
                      setDisplayedMetricValue(target, "amplitude", value, "amplitude_mv");
                    })
                )}
              </td>
              <td>
                {renderNumberInput(getDisplayedMetricValue(segment, "cv"), (value) =>
                  updateMotorSegment(nerve, index, (target) => {
                    setDisplayedMetricValue(target, "cv", value);
                  })
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!motorRows.length && <div className="table-placeholder">No motor reference values found.</div>}
    </div>
  );

  const renderSensoryTable = () => (
    <div className="table-shell ref-table-shell">
      <table className="app-table ref-table">
        <thead>
          <tr>
            <th>Nerve</th>
            <th>Stim</th>
            <th>Rec</th>
            <th>Lat(ms)</th>
            <th>Amp(uV)</th>
            <th>CV(m/s)</th>
          </tr>
        </thead>
        <tbody>
          {sensoryRows.map(({ rowKey, index, segment }) => (
            <tr key={rowKey}>
              <td>{segment.nerve}</td>
              <td>{segment.stimulation_site}</td>
              <td>{segment.recording_site}</td>
              <td>
                {renderNumberInput(getDisplayedMetricValue(segment, "latency"), (value) =>
                  updateSensorySegment(index, (target) => {
                    setDisplayedMetricValue(target, "latency", value);
                  })
                )}
              </td>
              <td>
                {renderNumberInput(
                  getDisplayedMetricValue(segment, "amplitude", "amplitude_uv"),
                  (value) =>
                    updateSensorySegment(index, (target) => {
                      setDisplayedMetricValue(target, "amplitude", value, "amplitude_uv");
                    })
                )}
              </td>
              <td>
                {renderNumberInput(getDisplayedMetricValue(segment, "cv"), (value) =>
                  updateSensorySegment(index, (target) => {
                    setDisplayedMetricValue(target, "cv", value);
                  })
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!sensoryRows.length && (
        <div className="table-placeholder">No sensory reference values found.</div>
      )}
    </div>
  );

  const renderSpecialTable = (
    rows: Array<{ rowKey: string; index: number; segment: ReferenceSegment }>,
    section: "F_wave" | "H_reflex",
    emptyText: string
  ) => (
    <div className="table-shell ref-table-shell">
      <table className="app-table ref-table">
        <thead>
          <tr>
            <th>Nerve</th>
            <th>Stim</th>
            <th>Rec</th>
            <th>Lat(ms)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ rowKey, index, segment }) => (
            <tr key={rowKey}>
              <td>{segment.nerve}</td>
              <td>{segment.stimulation_site}</td>
              <td>{segment.recording_site}</td>
              <td>
                {renderNumberInput(getDisplayedMetricValue(segment, "latency"), (value) =>
                  updateSpecialSegment(section, index, (target) => {
                    setDisplayedMetricValue(target, "latency", value);
                  })
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <div className="table-placeholder">{emptyText}</div>}
    </div>
  );

  const renderActiveSection = () => {
    switch (activeTab) {
      case "motor":
        return renderMotorTable();
      case "sensory":
        return renderSensoryTable();
      case "fwave":
        return renderSpecialTable(fwaveRows, "F_wave", "No F-wave reference values found.");
      case "hreflex":
        return renderSpecialTable(hreflexRows, "H_reflex", "No H-reflex reference values found.");
      default:
        return null;
    }
  };

  return (
    <div className="app-shell">
      <div className="dashboard-shell ref-shell">
        <div className="dashboard-topbar">
          <button
            type="button"
            className="app-button secondary small"
            onClick={() => navigate("/")}
          >
            Back to Workspace
          </button>
          {username && <span className="user-chip">{username}</span>}
          <AccountActions />
        </div>

        <header className="dashboard-hero">
          <div className="dashboard-hero-copy">
            <h1 className="dashboard-title">Reference Values</h1>
            <div className="hero-meta">
              <span className="hero-pill">{savedProfile?.rule_set || "default"}</span>
              <span className="hero-pill">Saved {formatSavedAt(savedProfile?.updated_at || null)}</span>
            </div>
          </div>
        </header>

        <section className="surface-card ref-card">
          <input
            ref={importInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: "none" }}
            onChange={handleImportFileChange}
          />

          <div className="ref-toolbar">
            <input
              type="text"
              className="app-input ref-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search nerve, stimulation site, or recording site"
            />

            <div className="toolbar">
              <button
                type="button"
                className="app-button secondary small"
                onClick={handleExport}
                disabled={loading || actionsBusy}
              >
                {exporting ? "Exporting..." : "Export JSON"}
              </button>
              <button
                type="button"
                className="app-button secondary small"
                onClick={handleImportClick}
                disabled={loading || actionsBusy}
              >
                {importing ? "Importing..." : "Import JSON"}
              </button>
              <button
                type="button"
                className="app-button secondary small"
                onClick={handleReload}
                disabled={!hasChanges || actionsBusy}
              >
                Reset
              </button>
              <button
                type="button"
                className="app-button primary small"
                onClick={handleSave}
                disabled={!hasChanges || actionsBusy}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="ref-tabs">
            {tabItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`ref-tab ${activeTab === item.key ? "active" : ""}`}
                onClick={() => setActiveTab(item.key)}
              >
                {item.label} {item.count}
              </button>
            ))}
          </div>

          {!loading && draftValues && (
            <>
              <div className="ref-age-toolbar">
                <div className="ref-age-tabs">
                  {!ageBands.length && (
                    <button type="button" className="ref-age-tab active">
                      Universal
                    </button>
                  )}

                  {ageBands.map((bandKey) => (
                    <button
                      key={bandKey}
                      type="button"
                      className={`ref-age-tab ${activeAgeBand === bandKey ? "active" : ""}`}
                      onClick={() => setActiveAgeBand(bandKey)}
                    >
                      {bandKey}
                    </button>
                  ))}
                </div>

                <div className="toolbar">
                  {!!ageBands.length && (
                    <button
                      type="button"
                      className="app-button secondary small"
                      onClick={handleResetToUniversal}
                    >
                      Back to Universal
                    </button>
                  )}
                  <button
                    type="button"
                    className="app-button secondary small"
                    onClick={handleSplitCurrentTab}
                  >
                    {ageBands.length ? "Split Current Tab" : "Enable Age Tabs"}
                  </button>
                </div>
              </div>

              {!!ageBands.length && activeAgeBand && activeAgeDraft && (
                <div className="ref-age-config">
                  <label className="ref-age-config-field">
                    <span>From</span>
                    <input
                      type="number"
                      className="app-input ref-small-input"
                      value={activeAgeDraft.min}
                      onChange={(event) => handleBandDraftChange({ min: event.target.value })}
                    />
                  </label>

                  <label className="ref-age-config-field">
                    <span>To</span>
                    <input
                      type="number"
                      className="app-input ref-small-input"
                      value={activeAgeDraft.max}
                      onChange={(event) => handleBandDraftChange({ max: event.target.value })}
                    />
                  </label>

                  <div className="ref-age-hint">
                    Leave `To` blank if you want the tab to stay open-ended.
                  </div>

                  <button
                    type="button"
                    className="app-button secondary small"
                    onClick={commitActiveBandRange}
                  >
                    Apply Range
                  </button>
                </div>
              )}
            </>
          )}

          {loading && <div className="table-placeholder">Loading reference values...</div>}
          {!loading && error && <div className="auth-error">{error}</div>}
          {!loading && success && <div className="ref-success">{success}</div>}
          {!loading && !error && draftValues && renderActiveSection()}
        </section>
      </div>
    </div>
  );
};

export default ReferenceValuesPage;
